import { useCallback } from 'react';

interface NotificationOptions {
  message: string;
  type?: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
}

export const useModernNotification = () => {
  const showNotification = useCallback((options: NotificationOptions) => {
    const { message, type = 'info', duration = 3000 } = options;

    const notification = document.createElement('div');

    const typeStyles = {
      success: {
        gradient: 'from-green-500 to-emerald-600',
        icon: '✅',
      },
      error: {
        gradient: 'from-red-500 to-red-600',
        icon: '❌',
      },
      warning: {
        gradient: 'from-yellow-500 to-orange-600',
        icon: '⚠️',
      },
      info: {
        gradient: 'from-blue-500 to-blue-600',
        icon: 'ℹ️',
      },
    };

    const style = typeStyles[type];

    notification.className = `
      fixed top-4 right-4 z-50 bg-gradient-to-r ${style.gradient}
      text-white px-6 py-4 rounded-2xl shadow-2xl border-2 border-white/20 
      backdrop-blur-sm transform transition-all duration-500 animate-slideInRight
      min-w-80 max-w-md
    `;

    notification.style.animation = 'slideInRight 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)';

    notification.innerHTML = `
      <div class="flex items-center gap-3">
        <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
          <span class="text-lg">${style.icon}</span>
        </div>
        <span class="font-semibold text-base">${message}</span>
      </div>
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'slideOutRight 0.3s ease-in forwards';
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, duration);
  }, []);

  const showSuccess = useCallback(
    (message: string) => {
      showNotification({ message, type: 'success' });
    },
    [showNotification]
  );

  const showError = useCallback(
    (title: string, message?: string) => {
      const fullMessage = message ? `${title}: ${message}` : title;
      showNotification({ message: fullMessage, type: 'error', duration: 5000 });
    },
    [showNotification]
  );

  const showWarning = useCallback(
    (message: string) => {
      showNotification({ message, type: 'warning' });
    },
    [showNotification]
  );

  const showInfo = useCallback(
    (message: string) => {
      showNotification({ message, type: 'info' });
    },
    [showNotification]
  );

  return {
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};
